import RegionSet, { RegionGranularity } from "../regionSet";

/** The general structure of the children inside a LookupRegionSet. */
interface LookupRegionSetChildren { [key: string]: LookupRegionSet; }

/**
 * A LookupRegionSet is a morphing of the standard RegionSet in favor of fast lookups.
 * It, in particular, transforms the children into a dictionary structure keyed off their (region code, granularity) combination.
 */
export default class LookupRegionSet {
  constructor(
    public regionCode: string,
    public granularity: RegionGranularity,
    public children: LookupRegionSetChildren = {},
    public subRegionCode: string = null,
  ) {
  }

  /** Produces the key for the lookup. */
  public get key(): string {
    return keyFor(this.regionCode, this.granularity, this.subRegionCode);
  }
}

/**
 * Produces the access key for a given region code and granularity to which it applies.
 * @param regionCode The region code in question.
 * @param granularity The granularity that the region code is a descriptor for.
 * @param subRegionCode An optional sub-region code for county-zips.
 */
function keyFor(regionCode: string, granularity: RegionGranularity, subRegionCode?: string): string;

/**
 * Produces the access key for a given region set.
 * @param regionSet The region set in question.
 */
function keyFor(regionSet: RegionSet): string;

/**
 * Produces an access key for a given entity, either string or RegionSet.
 */
function keyFor(entity: string | RegionSet, granularity?: RegionGranularity, subRegionCode?: string): string {
  if (typeof entity === "string" || !entity) {
    return subRegionCode
      ? `(${entity}, ${subRegionCode}, ${granularity})`
      : `(${entity}, ${granularity})`;
  }

  return entity.granularity === RegionGranularity.CountyZip
    ? `(${entity.regionCode}, ${entity.subRegionCode}, ${entity.granularity})`
    : `(${entity.regionCode}, ${entity.granularity})`;
}

export {
  keyFor,
};
